import styled from 'styled-components';
import Link from 'next/link';

import { config } from '_config';
import { theme } from '_constants/theme';
import { ICONS_ALIASES, ICONS_PALETTE } from '_constants/icons';
import { ROUTES } from '_constants/routes';
import { Payroll_FAQ } from '_constants/faq';

import { Divider } from '_components/atomic/layouts';
import Circle from '_components/atomic/Circle';
import Dots from '_components/atomic/Dots';
import FancyList from '_components/atomic/FancyList';
import Icon from '_components/atomic/Icon';
import Button from '_components/atomic/Button';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import BottomNavigationSection from '_components/composed/sections/BottomNavigationSection';
import FAQSection from '_components/composed/sections/FAQSection';
import HowSection from '_components/composed/sections/HowSection';
import ProductBannerSection from '_components/composed/sections/ProductBannerSection';

const { colors, spaces, fontSizes, devices } = theme;

const StyledPayrollPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;

  .link-container {
    width: 100%;
  }

  .buttonsContainer {
    a {
      text-decoration: none;
    }
  }
`;

const StyleButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${spaces.regular}px;
  gap: ${spaces.regular}px;
  background: ${colors.indigo[500]};
  border-radius: ${spaces.regular}px;

  .price {
    font-family: 'Aeonik';
    font-weight: 500;
    font-size: ${fontSizes.large2X}px;
    line-height: 150%;
    color: ${colors.indigo[25]};
  }

  @media ${devices.tablet} {
    flex-direction: column;
    .price {
      font-size: ${fontSizes.regular}px;
    }
  }
`;

const PayrollPage = () => {
  return (
    <StyledPayrollPage>
      <ProductBannerSection
        title="Payroll"
        text="Payroll is an important aspect of any business, and managing it effectively can be time-consuming and complex. Our payroll service offers a solution, simplifying the payroll process and freeing up valuable time for business owners and managers. With our comprehensive service, tax calculations, direct deposit, and all other payroll-related tasks are handled with ease, giving businesses the peace of mind they need to focus on growth and success. Whether you're just starting out or looking to improve your existing payroll system, our payroll service is the ideal solution for your business."
        buttonsBlock={
          <div className="link-container">
            <Link href={config.payrollUrl}>
              <Button
                suffix={
                  <Icon
                    alias={ICONS_ALIASES.arrowRight}
                    color={colors.indigo[600]}
                  />
                }
                color={colors.white}
                textColor={colors.indigo[600]}
                hoverColor={colors.white}
                hoverTextColor={colors.indigo[600]}
              >
                Get Started Now
              </Button>
            </Link>
          </div>
        }
        backgroundImage={`${config.basePath}/images/payroll-banner-background.svg`}
        desktopImage={`${config.basePath}/images/payroll-banner-desktop.png`}
        mobileImage={`${config.basePath}/images/payroll-banner-mobile.png`}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <HowSection
        title={<>How it Helps</>}
        rightPart={
          <FancyList
            items={[
              {
                title: 'Tax calculations and compliance',
                description:
                  'Automatically calculates and withhold taxes based on local, state, and federal regulations.',
                icon: ICONS_ALIASES.taxes2,
                iconPalette: ICONS_PALETTE.indigo[ICONS_ALIASES.taxes2],
                color: colors.indigo[25],
                textColor: colors.indigo[500],
              },
              {
                title: 'Direct deposit',
                description:
                  'Automatically transfers employee salaries directly into their bank accounts, saving time and reducing the risk of errors.',
                icon: ICONS_ALIASES.cash,
                iconPalette: ICONS_PALETTE.green[ICONS_ALIASES.cash],
                color: colors.green[50],
                textColor: colors.green[500],
              },
              {
                title: 'Employee self-service portal',
                description:
                  'Allows employees to access their pay stubs, tax forms, and other important information online, reducing the workload for the employer.',
                icon: ICONS_ALIASES.onlinePayment,
                iconPalette: ICONS_PALETTE.blue[ICONS_ALIASES.onlinePayment],
                color: colors.blue[25],
                textColor: colors.blue[500],
              },
              {
                title: 'Payroll reporting and record-keeping',
                description:
                  'Generates reports and maintains records of all payroll transactions, making it easier to track expenses and meet regulatory requirements.',
                icon: ICONS_ALIASES.seoReport,
                iconPalette: ICONS_PALETTE.orange[ICONS_ALIASES.seoReport],
                color: colors.orange[50],
                textColor: colors.orange[500],
              },
              {
                title: 'Customizable and flexible',
                description:
                  'Payroll services can be customized to meet the specific needs of each business, including support for different pay frequencies and salary structures.',
                icon: ICONS_ALIASES.adaptation,
                iconPalette: ICONS_PALETTE.indigo[ICONS_ALIASES.adaptation],
                color: colors.indigo[25],
                textColor: colors.indigo[500],
              },
            ]}
          />
        }
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <BottomBannerSection
        header={<>Take Control of Your Payroll</>}
        footer={
          <div className="buttonsContainer">
            <Link href={config.payrollUrl}>
              <StyleButtonContainer>
                <Button
                  color={colors.white}
                  textColor={colors.indigo[600]}
                  hoverColor={colors.white}
                  hoverTextColor={colors.indigo[600]}
                >
                  Get Started Now
                </Button>
                <div className="price">Just $49.99/month</div>
              </StyleButtonContainer>
            </Link>
          </div>
        }
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <FAQSection items={Payroll_FAQ} />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <BottomNavigationSection
        previousService={{
          title: 'Registered Agent',
          link: ROUTES.registeredAgent,
        }}
        nextService={{
          title: 'LLC Incorporation',
          link: ROUTES.llcIncorporation,
        }}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <Dots
        color={theme.colors.indigo[500]}
        left={290}
        top={`calc(28% - 16px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        rotate={-90}
        left={49}
        top={`calc(21% - 20px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        right={260}
        top={`calc(32% + 30px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        right={-172}
        top={`calc(26% + 10px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        left={290}
        top={`calc(76% - 10px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        rotate={90}
        left={-134}
        top={`calc(68% - 0px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        right={296}
        top={`calc(60% + 28px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        right={-81}
        top={`calc(57% + 10px)`}
      />
    </StyledPayrollPage>
  );
};

export default PayrollPage;
